import React, { useState } from 'react';
import styles from './ExecutionLogCard.module.css';

/**
 * Mirrors the ProgressEvent shape emitted by agent-progress blocks.
 * Only fields relevant to the execution log are listed here.
 */
interface ProgressEvent {
  type?: string;
  status?: string;
  state?: string;
  tool?: string;
  stage?: string;
  detail?: string;
  taskId?: string;
  role?: string;
  feature?: string;
  completedTaskId?: string;
  targets?: { cloud?: string[]; public?: string[]; data?: string[] };
  changed?: { cloud?: string[]; public?: string[]; data?: string[] };
}

interface PhaseInfo {
  taskId: string;
  role: string;
  feature: string;
  completed: boolean;
  targetFiles: string[];
  changedFiles: string[];
}

function getRoleLabel(role?: string): string {
  const r = (role || '').toUpperCase();
  if (r === 'BACKEND_ENGINEER') return 'Backend';
  if (r === 'PRODUCT_ENGINEER') return 'Frontend';
  return role || '';
}

function flattenFileTargets(t?: { cloud?: string[]; public?: string[]; data?: string[] }): string[] {
  if (!t) return [];
  const all = [
    ...(Array.isArray(t.public) ? t.public : []),
    ...(Array.isArray(t.cloud) ? t.cloud : []),
    ...(Array.isArray(t.data) ? t.data : []),
  ].filter(Boolean);
  return Array.from(new Set(all));
}

/**
 * Groups raw progress events by taskId into a structured per-phase summary.
 */
function buildPhases(events: ProgressEvent[]): PhaseInfo[] {
  const map = new Map<string, PhaseInfo>();
  const order: string[] = [];

  for (const evt of events) {
    const tid = evt.taskId;
    if (!tid) continue;

    if (!map.has(tid)) {
      map.set(tid, {
        taskId: tid,
        role: '',
        feature: '',
        completed: false,
        targetFiles: [],
        changedFiles: [],
      });
      order.push(tid);
    }

    const phase = map.get(tid)!;

    if (evt.role && !phase.role) phase.role = evt.role;
    if (evt.feature && !phase.feature) phase.feature = evt.feature;

    if (evt.tool === 'phase_start' && evt.targets) {
      const files = flattenFileTargets(evt.targets);
      if (files.length && !phase.targetFiles.length) {
        phase.targetFiles = files;
      }
    }

    if (evt.tool === 'phase_done') {
      phase.completed = true;
      if (evt.changed) {
        const files = flattenFileTargets(evt.changed);
        if (files.length) phase.changedFiles = files;
      }
    }

    if (evt.tool === 'complete_task' && evt.status === 'done') {
      phase.completed = true;
    }
  }

  return order.map((tid) => map.get(tid)!);
}

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LogIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 8h8M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * ExecutionLogCard - Persistent collapsible execution summary shown in chat
 * after an EXECUTION round finishes.
 *
 * Displays phases with role badges, changed files (actual), and target files (planned).
 */
export default function ExecutionLogCard({ events }: { events: ProgressEvent[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const phases = React.useMemo(() => buildPhases(events), [events]);

  // Collect all unique files across phases (prefer changed over planned).
  const allUniqueFiles = React.useMemo(() => {
    const files = phases.flatMap((p) =>
      p.changedFiles.length > 0 ? p.changedFiles : p.targetFiles
    );
    return Array.from(new Set(files));
  }, [phases]);

  const fileCount = allUniqueFiles.length;

  if (!phases.length) return null;

  return (
    <div className={styles.card}>
      {/* Header: always visible, shows action count */}
      <div
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <LogIcon className={styles.headerIcon} />
        <span className={styles.headerTitle}>Execution log</span>
        <span className={styles.headerCount}>
          {fileCount} {fileCount === 1 ? 'file' : 'files'} modified
        </span>
        <svg
          className={styles.chevron}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Collapsed: show file chips inline */}
      {!isExpanded && allUniqueFiles.length > 0 && (
        <div className={styles.filesList} style={{ padding: '0 0.75rem 0.5rem' }}>
          {allUniqueFiles.slice(0, 5).map((f) => (
            <span key={f} className={styles.fileChip}>{f}</span>
          ))}
          {allUniqueFiles.length > 5 && (
            <span className={styles.fileChip} style={{ opacity: 0.6 }}>
              +{allUniqueFiles.length - 5} more
            </span>
          )}
        </div>
      )}

      {/* Expanded: full phase details */}
      {isExpanded && (
        <div className={styles.body}>
          {phases.map((phase) => {
            const roleLabel = getRoleLabel(phase.role);
            const isBackend = (phase.role || '').toUpperCase() === 'BACKEND_ENGINEER';
            const displayFiles = phase.changedFiles.length > 0 ? phase.changedFiles : phase.targetFiles;
            const filesLabel = phase.changedFiles.length > 0 ? 'Changed' : 'Planned';

            return (
              <div key={phase.taskId} className={styles.phase}>
                <div className={styles.phaseHeader}>
                  {phase.completed && <CheckIcon className={styles.phaseCheck} />}
                  <span className={styles.phaseName}>{phase.feature || phase.taskId}</span>
                  {roleLabel && (
                    <span className={isBackend ? styles.roleBadgeBackend : styles.roleBadgeFrontend}>
                      {roleLabel}
                    </span>
                  )}
                </div>

                {displayFiles.length > 0 && (
                  <div className={styles.filesSection}>
                    <div className={styles.filesLabel}>{filesLabel}</div>
                    <div className={styles.filesList}>
                      {displayFiles.map((f) => (
                        <span key={f} className={styles.fileChip}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
